import React from 'react';
import { Button } from '@zillow/constellation-10';
import { useWizard, useWizardStep, useWizardSubStep } from '@zillow/zrs-rental-applications';
import { useId, useIsOnExitLoading, useSkipLandingStep, useSkipTermsStep } from '../contexts/IdVerificationContext';
import type { ButtonProps } from '../NextButton/NextButton';
import { IdvStep } from '../../../models';
import { FROZEN_ACCOUNT_WARNING_SUBSTEP } from '../constants/steps';

export default function BackButton() {
  const id = useId();
  const skipLandingStep = useSkipLandingStep();
  const skipTermsStep = useSkipTermsStep();
  const { currentStep, currentSubStep, onRequestPrev, isLoading } = useWizard();
  const isOnExitLoading = useIsOnExitLoading();

  const currentStepDetails = useWizardStep({
    id: currentStep,
    subscription: ['renderBackButton', 'isBackDisabled'],
  });

  const currentSubStepDetails = useWizardSubStep({
    id: currentSubStep,
    stepId: currentStep,
    subscription: ['renderBackButton', 'isBackDisabled'],
  });

  const { renderBackButton: renderBackButtonFromStep, isBackDisabled: isBackDisabledStep = false } =
    currentStepDetails?.meta ?? {};

  const { renderBackButton: renderBackButtonFromSubStep, isBackDisabled: isBackDisabledSubStep = false } =
    currentSubStepDetails?.meta ?? {};

  const buttonProps: ButtonProps = {
    id: `${id}-back-button`,
    disabled: isBackDisabledSubStep || isBackDisabledStep || isLoading || isOnExitLoading,
    onClick: onRequestPrev,
    currentStep,
    currentSubStep,
  };

  const renderBackButton = renderBackButtonFromSubStep ?? renderBackButtonFromStep ?? 'Back';

  if (!currentStep || isLoading || (currentStepDetails?.hidden && currentSubStep != FROZEN_ACCOUNT_WARNING_SUBSTEP)) {
    return null;
  }

  switch (currentStep) {
    case IdvStep.TermsStep:
      if (skipLandingStep) {
        return null;
      }
      break;
    case IdvStep.PersonalInfoStep:
      if (skipLandingStep && skipTermsStep) {
        return null;
      }
      break;
  }

  if (typeof renderBackButton === 'string') {
    return (
      <Button tone="brand" emphasis="outlined" {...buttonProps}>
        {renderBackButton}
      </Button>
    );
  }

  if (typeof renderBackButton === 'function') {
    return renderBackButton(buttonProps);
  }

  return (
    <Button
      tone="brand"
      emphasis="outlined"
      onClick={onRequestPrev}
      disabled={isBackDisabledStep || isBackDisabledSubStep}
    >
      Back
    </Button>
  );
}
